import React, { useState } from 'react';
import { toast } from 'sonner';
import { History, Loader2, AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { historyImportAPI, HISTORY_IMPORT_MONTHS } from '@/lib/api';

/**
 * Seed this client's vendor patterns from their own books — on request, never on connect.
 *
 * Two rules shape this panel:
 *
 * 1. NOTHING IS WRITTEN UNTIL THE ACCOUNTANT SAYS SO. Preview reads their ledger and shows what it
 *    would write; the import button sends only the rows still ticked. Inheriting a client's coding
 *    is a judgement about their books, and it belongs to the person who will answer for it.
 * 2. WHAT WAS REJECTED IS SHOWN TOO. "We found nothing" and "we found a supplier coded three
 *    different ways and refused to guess" are different answers, and the second one tells the
 *    accountant something real about the books they have taken on.
 *
 * Imported mappings are explicitly labelled as unconfirmed wherever they surface later, so this
 * panel says so here as well rather than implying the patterns carry an accountant's authority.
 */
export function eligibleIds(preview) {
    return (preview?.proposals || []).map((p) => p.vendor_identifier);
}

export default function HistoryImportPanel({ tenant }) {
    const tenantId = tenant?.id;
    const [preview, setPreview] = useState(null);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [showRejected, setShowRejected] = useState(false);
    // Links the accountant has TICKED. Separate from `selected` on purpose: importing a mapping
    // and accepting that it refers to the same supplier as their invoices are two decisions, and
    // conflating them would make the second one happen silently as a side effect of the first.
    const [confirmedLinks, setConfirmedLinks] = useState({});

    const runPreview = async () => {
        setLoading(true);
        try {
            const { data } = await historyImportAPI.preview(tenantId, HISTORY_IMPORT_MONTHS);
            setPreview(data);
            setSelected(eligibleIds(data));
            setConfirmedLinks({});          // suggestions start UNCONFIRMED, always
            if (!data.proposals?.length) toast.info('No stable supplier mappings found');
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || 'Could not read this client’s history');
        } finally {
            setLoading(false);
        }
    };

    const runImport = async () => {
        setImporting(true);
        try {
            const links = Object.fromEntries(
                Object.entries(confirmedLinks).filter(([vid]) => selected.includes(vid)));
            const { data } = await historyImportAPI.apply(
                tenantId, selected, preview?.months || HISTORY_IMPORT_MONTHS, links);
            toast.success(`Imported ${data.written} supplier mapping${data.written === 1 ? '' : 's'}`);
            if (data.links_confirmed > 0) {
                toast.info(`${data.links_confirmed} linked to suppliers on your existing invoices`);
            }
            if (data.kept_accountant_patterns > 0) {
                toast.info(`${data.kept_accountant_patterns} left untouched — already confirmed by an accountant`);
            }
            setPreview(null);
            setSelected([]);
            setConfirmedLinks({});
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Import failed');
        } finally {
            setImporting(false);
        }
    };

    const toggle = (id) =>
        setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

    const toggleLink = (vid, docKey) =>
        setConfirmedLinks((m) => {
            const next = { ...m };
            if (next[vid]) delete next[vid]; else next[vid] = docKey;
            return next;
        });

    return (
        <div data-testid="history-import-panel" className="space-y-3">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-sm font-medium flex items-center gap-2">
                        <History className="h-4 w-4 shrink-0" /> Import coding history
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                        Read this client&rsquo;s own posted entries and suggest how their suppliers are
                        usually coded. Nothing is written until you approve it.
                    </p>
                </div>
                <Button size="sm" variant="outline" onClick={runPreview} disabled={loading || !tenantId}
                        data-testid="history-import-preview">
                    {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                    {loading ? 'Reading…' : 'Preview'}
                </Button>
            </div>

            {preview && (
                <div data-testid="history-import-preview-result" className="rounded border border-border p-3 space-y-3">
                    <p className="text-xs text-muted-foreground" data-testid="history-import-summary">
                        Read {preview.entries_read} entries since {preview.since} ·{' '}
                        {preview.counts?.eligible ?? 0} stable mapping
                        {(preview.counts?.eligible ?? 0) === 1 ? '' : 's'} ·{' '}
                        {preview.counts?.rejected ?? 0} too inconsistent to use
                    </p>

                    {preview.proposals?.length > 0 ? (
                        <>
                            <ul className="space-y-1">
                                {preview.proposals.map((p) => (
                                    <li key={p.vendor_identifier} className="space-y-1 text-xs">
                                        <div className="flex items-center gap-2">
                                        <Checkbox
                                            checked={selected.includes(p.vendor_identifier)}
                                            onCheckedChange={() => toggle(p.vendor_identifier)}
                                            aria-label={p.vendor_name}
                                        />
                                        <span className="flex-1 truncate">{p.vendor_name}</span>
                                        <span className="font-mono">{p.account_code}</span>
                                        <span className="font-mono w-16 text-right">{p.vat_code || '—'}</span>
                                        <span className="w-28 text-right text-muted-foreground">
                                            {p.observed_count}× · {Math.round((p.observed_dominance || 0) * 100)}%
                                        </span>
                                        </div>
                                        {p.suggested_link && (
                                            <label className="flex items-start gap-2 pl-6 text-[11px] text-muted-foreground"
                                                   data-testid={`history-import-link-${p.vendor_identifier}`}>
                                                <Checkbox
                                                    checked={!!confirmedLinks[p.vendor_identifier]}
                                                    onCheckedChange={() => toggleLink(p.vendor_identifier, p.suggested_link.vendor_identifier)}
                                                    aria-label={`Link ${p.vendor_name} to ${p.suggested_link.supplier_name}`}
                                                />
                                                <span>
                                                    <strong>{p.vendor_name}</strong> looks like the same supplier as your{' '}
                                                    <strong>{p.suggested_link.supplier_name}</strong> invoices
                                                    {p.suggested_link.documents ? ` (${p.suggested_link.documents})` : ''}.
                                                    Tick to link them — without this the mapping will not apply to documents.
                                                </span>
                                            </label>
                                        )}
                                    </li>
                                ))}
                            </ul>
                            <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                                <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
                                These are taken from the client&rsquo;s existing books and are
                                <strong className="mx-1">not confirmed</strong>
                                by an accountant. They will be used as suggestions only, and your own
                                corrections always take precedence.
                            </p>
                            <Button size="sm" onClick={runImport} disabled={importing || !selected.length}
                                    data-testid="history-import-apply">
                                {importing ? 'Importing…' : `Import ${selected.length} mapping${selected.length === 1 ? '' : 's'}`}
                            </Button>
                        </>
                    ) : (
                        <p className="text-xs text-muted-foreground" data-testid="history-import-empty">
                            No supplier was coded consistently enough to inherit.
                        </p>
                    )}

                    {preview.rejected?.length > 0 && (
                        <div>
                            <button type="button" className="text-[11px] underline text-muted-foreground"
                                    onClick={() => setShowRejected((v) => !v)}
                                    data-testid="history-import-toggle-rejected">
                                {showRejected ? 'Hide' : 'Show'} {preview.rejected.length} inconsistent supplier
                                {preview.rejected.length === 1 ? '' : 's'}
                            </button>
                            {showRejected && (
                                <ul className="mt-1 space-y-0.5" data-testid="history-import-rejected">
                                    {preview.rejected.map((p) => (
                                        <li key={p.vendor_identifier} className="text-[11px] text-muted-foreground">
                                            {p.vendor_name} — {p.observed_count} entries, most common{' '}
                                            {p.account_code} at {Math.round((p.observed_dominance || 0) * 100)}%
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
